// Web equivalents for React Native dependencies
// AsyncStorage -> localStorage
// NetInfo -> navigator.onLine
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'; 

export const SERVER_URL = "https://www.giftmkr.com";
// export const SERVER_URL = "http://mocn.lenwork.cn";
// export const SERVER_URL = "http://localhost:3001";
const API_URL = SERVER_URL + "/api/";

 

interface CacheItem {
  data: any;
  timestamp: number;
  expiresIn: number;
}

interface FailedRequest {
  config: AxiosRequestConfig;
  timestamp: number;
}

const CACHE_PREFIX = 'api_cache_';
const FAILED_REQUESTS_KEY = 'failed_requests';
const TOKEN_KEY = 'auth_token';
const DEFAULT_CACHE_TIME = 5 * 60 * 1000; // 5 minutes
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 1000; // 1 second

// 存储失败的请求
const failedRequests: FailedRequest[] = [];

// 从 localStorage 加载失败的请求
const loadFailedRequests = async (): Promise<void> => {
  try {
    const stored = localStorage.getItem(FAILED_REQUESTS_KEY);
    if (stored) {
      failedRequests.push(...JSON.parse(stored));
    }
  } catch (error) {
    console.error('Error loading failed requests:', error);
  }
};

// 保存失败的请求到 localStorage
const saveFailedRequests = async (): Promise<void> => {
  try {
    localStorage.setItem(FAILED_REQUESTS_KEY, JSON.stringify(failedRequests));
  } catch (error) {
    console.error('Error saving failed requests:', error);
  }
};

// 添加失败的请求
const addFailedRequest = async (config: AxiosRequestConfig): Promise<void> => {
  failedRequests.push({
    config,
    timestamp: Date.now(),
  });
  await saveFailedRequests();
};

// 移除失败的请求
const removeFailedRequest = async (config: AxiosRequestConfig): Promise<void> => {
  const index = failedRequests.findIndex(
    req => generateCacheKey(req.config) === generateCacheKey(config)
  );
  if (index !== -1) {
    failedRequests.splice(index, 1);
    await saveFailedRequests();
  }
};

// 重试失败的请求
const retryFailedRequests = async (): Promise<void> => {
  if (failedRequests.length === 0) return;

  console.log('Syncing data after network recovery...');
  
  const requests = [...failedRequests];
  failedRequests.length = 0; // 清空数组
  await saveFailedRequests();

  for (const { config } of requests) {
    try {
      await request(config);
    } catch (error) {
      // 如果重试失败，重新添加到失败队列
      await addFailedRequest(config);
    }
  }
};

// 监听网络状态变化
window.addEventListener('online', () => {
    // 网络恢复
    retryFailedRequests();
});

// 初始化时加载失败的请求
loadFailedRequests();

// 生成缓存键
const generateCacheKey = (config: AxiosRequestConfig): string => {
  const { url, method, params, data } = config;
  return `${CACHE_PREFIX}${method}_${url}_${JSON.stringify(params)}_${JSON.stringify(data)}`;
};

// 获取缓存数据
const getCache = async (key: string): Promise<CacheItem | null> => {
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return null;
    const cacheItem: CacheItem = JSON.parse(cached);
    const now = Date.now();
    if (now - cacheItem.timestamp > cacheItem.expiresIn) {
      localStorage.removeItem(key);
      return null;
    }
    return cacheItem;
  } catch (error) {
    console.error('Error getting cache:', error);
    return null;
  }
};

// 设置缓存数据
const setCache = async (key: string, data: any, expiresIn: number = DEFAULT_CACHE_TIME): Promise<void> => {
  try {
    const cacheItem: CacheItem = {
      data,
      timestamp: Date.now(),
      expiresIn,
    };
    localStorage.setItem(key, JSON.stringify(cacheItem));
  } catch (error) {
    console.error('Error setting cache:', error);
  }
};

// 获取认证token
const getAuthToken = async (): Promise<string | null> => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (error) {
    console.error('Error getting auth token:', error);
    return null;
  }
};

const request = axios.create({
  baseURL: API_URL,
  timeout: 20000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
request.interceptors.request.use(
  async (config) => {
    // 添加认证token
    const token = await getAuthToken(); 
    if (token) {
      config.headers['authorization'] = `${token}`;
    }
    // 检查是否需要缓存
    const shouldCache = false;//config.method?.toLowerCase() === 'get' && config.cache !== false;
 
    if (shouldCache) {
      const cacheKey = generateCacheKey(config);
      const cachedData = await getCache(cacheKey);
      
      if (cachedData) {
        // 如果有缓存数据，直接返回
        return Promise.reject({
          __CACHE__: true,
          data: cachedData.data,
          config: config
        });
      }
    } 
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 跳转到登录页面的函数
const redirectToLogin = async () => {
  try {
    // 清除用户信息和token
    localStorage.removeItem('currentUser');
    localStorage.removeItem(TOKEN_KEY);
    console.log('Redirecting to login page...');
    
    // 使用导航服务跳转到登录页面
    // navigateToLogin();
  } catch (error) {
    console.error('Error redirecting to login:', error);
  }
};

// Response interceptor
request.interceptors.response.use(
  async (response: any) => {  
    if (response.data.code === 401) {
      await redirectToLogin();
      return Promise.reject(new Error('Authentication failed'));
    } 

    const res = response.data;
    // 处理缓存数据
    if (response.config.method?.toLowerCase() === 'get' && response.config.cache !== false) {
      const cacheKey = generateCacheKey(response.config);
      await setCache(cacheKey, res);
    }
    // 请求成功，从失败队列中移除
    await removeFailedRequest(response.config);
    if (!res.success) {
      console.log(res.message || 'Request failed');
      return Promise.reject(new Error(res.message || 'Request failed'));
    }
    return res;
  },
  async (error) => {
    console.log('error..........', error);
    // 处理缓存    
    if (error.__CACHE__) {
      return {
        data: error.data,
        status: 200,
        statusText: 'OK',
        headers: {},
        config: error.config
      } as any;
    }
    if (error.code=="NETWORK_ERROR") {
      return Promise.reject(error);
    }
    // 检查是否是认证错误（401）
    if (error.response?.status === 401) {
      console.log('Authentication failed, redirecting to login...');
      await redirectToLogin(); 
    }

    return error.response.data;
    
    // // 检查是否离线
    // if (!navigator.onLine) {
    //   console.log('You are offline. Using cached data if available.');
    //   const cacheKey = generateCacheKey(error.config);
    //   const cachedData = await getCache(cacheKey);
    //   if (cachedData) {
    //     return {
    //       data: cachedData.data,
    //       status: 200,
    //       statusText: 'OK',
    //       headers: {},
    //       config: error.config
    //     } as any;
    //   }
    //   // 添加到失败队列
    //   await addFailedRequest(error.config);
    // }
    // console.log(error.message || 'Request failed');
    // return Promise.reject(error.response.result);
  }
);

// 添加重试机制
const retryRequest = async (config: AxiosRequestConfig, retryCount = 0): Promise<any> => {
  try {
    return await request(config);
  } catch (error: any) { 
    // 只有网络错误才重试
    const isNetworkError = !navigator.onLine || 
                          error.code === 'NETWORK_ERROR' || 
                          error.code === 'ECONNABORTED' ||
                          error.message?.includes('Network Error') ||
                          error.message?.includes('timeout');
    
    if (isNetworkError && retryCount < MAX_RETRY_ATTEMPTS) {
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (retryCount + 1)));
      return retryRequest(config, retryCount + 1);
    }
    throw error;
  }
};

// 扩展 request 函数
const enhancedRequest = async (config: AxiosRequestConfig): Promise<any> => {
  try {
    const response = await retryRequest(config);
    return response;  // 直接返回 response，因为 response 已经是处理后的数据
  } catch (error: any) {
    // 只有网络错误才添加到失败队列
    const isNetworkError = !navigator.onLine || 
                          error.code === 'NETWORK_ERROR' || 
                          error.message?.includes('timeout');
    
    if (isNetworkError) {
      await addFailedRequest(config);
    }
    throw error;
  }
};

// 扩展 AxiosRequestConfig 类型
declare module 'axios' {
  interface AxiosRequestConfig {
    cache?: boolean;
  }
}

export default enhancedRequest;
export { request, TOKEN_KEY }  ; 